import { useEffect, useState } from 'react'
import { uploadMemberMedia } from '../utils/media'
import { C, fontStack, btn } from '../constant/theme'

const CONCURRENCY = 3

// Mobile-friendly upload for trip members (and admins): pick a day, choose
// photos, and they upload into that day. Admins can also upload videos.
export default function MemberUpload({ slug, days, isAdmin, onClose, onUploaded }) {
  const [dayId, setDayId] = useState(days[0]?._id || '')
  const [uploads, setUploads] = useState([]) // { key, name, progress, ok, error }
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const onFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => isAdmin ? (f.type.startsWith('image/') || f.type.startsWith('video/')) : f.type.startsWith('image/'))
    if (!dayId || !files.length) return
    const jobs = files.map((file, i) => ({ key: `${file.name}-${i}-${Date.now()}-${Math.random()}`, file }))
    setUploads((u) => [...u, ...jobs.map((j) => ({ key: j.key, name: j.file.name, progress: 0 }))])
    setBusy(true)

    const uploadOne = async ({ key, file }) => {
      try {
        await uploadMemberMedia({ slug, dayId, file, onProgress: (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))) })
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: 1, ok: true } : x)))
      } catch {
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)))
      }
    }

    let cursor = 0
    const worker = async () => { while (cursor < jobs.length) await uploadOne(jobs[cursor++]) }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker))
    setBusy(false)
    onUploaded && onUploaded()
  }

  const doneCount = uploads.filter((u) => u.ok).length

  return (
    <div style={S.backdrop} onClick={busy ? undefined : onClose}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <strong style={{ color: C.text, fontSize: 16 }}>Add photos</strong>
          <button style={S.close} onClick={onClose} disabled={busy}>✕</button>
        </div>

        <div style={S.bodyArea}>
          <label style={S.label}>Which day?</label>
          <select style={S.select} value={dayId} onChange={(e) => setDayId(e.target.value)} disabled={busy}>
            {days.map((d) => (
              <option key={d._id} value={d._id}>{d.title || d.date || 'Day'}</option>
            ))}
          </select>

          <label style={{ ...btn('primary'), ...S.pickBtn, ...(!dayId ? S.disabled : {}) }}>
            📷 {isAdmin ? 'Choose photos / videos' : 'Choose photos'}
            <input type="file" accept={isAdmin ? 'image/*,video/*' : 'image/*'} multiple hidden disabled={!dayId} onChange={(e) => onFiles(e.target.files)} />
          </label>
          <p style={S.note}>You can pick many at once. They'll be added to the selected day.</p>

          {uploads.length > 0 && (
            <div style={S.list}>
              <div style={S.listHead}>{doneCount}/{uploads.length} uploaded</div>
              {uploads.map((u) => (
                <div key={u.key} style={S.row}>
                  <span style={S.rowName}>{u.name}</span>
                  <span style={S.rowStatus}>
                    {u.error ? '⚠️ failed' : u.ok ? '✓' : `${Math.round(u.progress * 100)}%`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button style={btn('ghost')} onClick={onClose} disabled={busy}>{busy ? 'Uploading…' : 'Done'}</button>
        </div>
      </div>
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(8,14,24,0.55)', zIndex: 11000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', fontFamily: fontStack },
  sheet: { width: '100%', maxWidth: 520, maxHeight: '90vh', background: C.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -8px 40px rgba(8,14,24,.4)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  close: { width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#d0d8e4', color: C.text, cursor: 'pointer', fontSize: 14 },
  bodyArea: { padding: 18, overflowY: 'auto' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3d52', marginBottom: 6 },
  select: { width: '100%', boxSizing: 'border-box', padding: '12px', border: `1.5px solid ${C.inputBorder}`, borderRadius: 10, background: C.inputBg, fontSize: 15, color: C.text, fontFamily: 'inherit', marginBottom: 16 },
  pickBtn: { display: 'block', textAlign: 'center', padding: '15px', fontSize: 15.5, cursor: 'pointer' },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  note: { fontSize: 12.5, color: C.textSoft, margin: '10px 2px 0' },
  list: { marginTop: 18, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, overflow: 'hidden' },
  listHead: { fontSize: 12.5, fontWeight: 700, color: C.muted, padding: '10px 14px', textTransform: 'uppercase', letterSpacing: '.04em' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderTop: `1px solid ${C.cardBorder}` },
  rowName: { fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  rowStatus: { fontSize: 13, fontWeight: 700, color: C.navy2, flexShrink: 0 },
  footer: { padding: '14px 18px', background: '#fff', borderTop: `1px solid ${C.cardBorder}`, display: 'flex', justifyContent: 'flex-end' },
}
